import React, { Suspense } from "react";
import { ChakraProvider, Spinner, Center } from "@chakra-ui/react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ApolloProvider } from "@apollo/client";
import { apolloClient } from "./apollo/client";
import Home from "./pages/Home";
import LoginPage from "./pages/authentication/LoginPage";
import { brandConfig } from "./brandConfig";
import theme from "./theme";

// Module configurations - LinkOSx: 8 modules
import clientsModuleConfig from "./pages/clients/moduleConfig";
import employeesModuleConfig from "./pages/employees/moduleConfig";
import opportunitiesModuleConfig from "./pages/opportunities/moduleConfig";
import proposalsModuleConfig from "./pages/proposals/moduleConfig";
import billsModuleConfig from "./pages/bills/moduleConfig";
import lmModuleConfig from "./pages/lm/moduleConfig";
import locationsModuleConfig from "./pages/locations/moduleConfig";
import shiftsModuleConfig from "./pages/shifts/moduleConfig";
import workersModuleConfig from "./pages/workers/moduleConfig";

// Combine all module configurations
const modules = [
  workersModuleConfig,
  clientsModuleConfig,
  employeesModuleConfig,
  opportunitiesModuleConfig,
  proposalsModuleConfig,
  billsModuleConfig,
  lmModuleConfig,
  locationsModuleConfig,
  shiftsModuleConfig,
];

// Protected Route Component
import { ProtectedRoute } from "./components/ProtectedRoute";

// Loading component for lazy-loaded routes
const LoadingFallback = () => (
  <Center h="100vh">
    <Spinner size="xl" color={brandConfig.colors.primary} />
  </Center>
);

// Special bill pages
import BillPaymentSuccess from "./pages/bills/BillPaymentSuccess";
const SubscribePage = React.lazy(() => import("./pages/bills/SubscribePage"));

// Contexts and Layout
import { AuthProvider } from "./contexts/AuthContext";
import { ModulePermissionsProvider, useModulePermissions } from "./contexts/ModulePermissionsContext";
import { IpfsGatewayLoader } from "./components/IpfsGatewayLoader";

// Profile-related imports
import ViewProfile from "./pages/profile/ViewProfile";
import EditProfile from "./pages/profile/EditProfile";
import Settings from "./pages/profile/Settings";

// Cheatsheets (admin-only)
import CheatsheetIndex from "./pages/cheatsheets/CheatsheetIndex";
import Phase1Permissions from "./pages/cheatsheets/Phase1Permissions";

// Updates (public)
const WorkersModuleLaunch = React.lazy(() => import("./pages/updates/WorkersModuleLaunch"));
const GettingStartedGuide = React.lazy(() => import("./pages/updates/GettingStartedGuide"));

// Floating navbar component
import { FloatingNavbar } from "./components/FloatingNavbar";
import { useAuth } from "./contexts/AuthContext";

const AppContent = () => {
  const { isAuthenticated } = useAuth();
  const { permissions: backendPerms } = useModulePermissions();

  // Resolve permissionLevel -> actual permission strings from backend
  const resolvedModules = React.useMemo(() => {
    return modules.map(module => {
      const modulePerms = backendPerms[module.id];
      if (!modulePerms) return module;

      return {
        ...module,
        routes: module.routes?.map((route: any) => {
          const level = (route as any).permissionLevel;
          if (!level) return route;
          return { ...route, permissions: (modulePerms as any)[level] || [] };
        }),
        navigation: module.navigation?.map((navItem: any) => {
          const level = (navItem as any).permissionLevel;
          if (!level) return navItem;
          return { ...navItem, permissions: (modulePerms as any)[level] || [] };
        }),
        permissions: {
          ...module.permissions,
          view: modulePerms.view || module.permissions.view,
          create: modulePerms.create || module.permissions.create,
          edit: modulePerms.edit || module.permissions.edit,
          delete: modulePerms.delete || module.permissions.delete,
          ...(modulePerms.financial ? { financial: modulePerms.financial } : {}),
        },
      };
    });
  }, [backendPerms]);

  return (
    <>
      {/* Show floating navbar only when authenticated */}
      {isAuthenticated && <FloatingNavbar modules={resolvedModules} />}

      <Routes>
        {/* Home page */}
        <Route path="/" element={<Home />} />

        {/* Login page */}
        <Route path="/login" element={<LoginPage />} />

        {/* Dynamic Module Routes */}
        {resolvedModules.map((module) =>
          module.routes?.map((route: any) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                (route as any).public ? (
                  <Suspense fallback={<LoadingFallback />}>
                    <route.component />
                  </Suspense>
                ) : (
                  <ProtectedRoute requiredPermissions={route.permissions || []}>
                    <Suspense fallback={<LoadingFallback />}>
                      <route.component />
                    </Suspense>
                  </ProtectedRoute>
                )
              }
            />
          ))
        )}

        {/* Special bill routes */}
        <Route path="/bill/:id/payment-success" element={<BillPaymentSuccess />} />
        <Route path="/subscribe/:slug" element={<SubscribePage />} />

        {/* Profile routes */}
        <Route path="/profile" element={<ViewProfile />} />
        <Route path="/profile/edit" element={<EditProfile />} />
        <Route path="/profile/settings" element={<Settings />} />

        {/* Updates (public) */}
        <Route path="/updates/workers-module-launch" element={
          <Suspense fallback={<LoadingFallback />}>
            <WorkersModuleLaunch />
          </Suspense>
        } />
        <Route path="/updates/getting-started" element={
          <Suspense fallback={<LoadingFallback />}>
            <GettingStartedGuide />
          </Suspense>
        } />

        {/* Cheatsheets (admin-only) */}
        <Route path="/cheatsheets" element={
          <ProtectedRoute requiredPermissions={["ADMIN"]}>
            <CheatsheetIndex />
          </ProtectedRoute>
        } />
        <Route path="/cheatsheets/phase1-permissions" element={
          <ProtectedRoute requiredPermissions={["ADMIN"]}>
            <Phase1Permissions />
          </ProtectedRoute>
        } />
      </Routes>
    </>
  );
};

export const App = () => {
  // Check and set tenantId if missing
  React.useEffect(() => {
    const storedTenantId = localStorage.getItem('tenantId');
    if (!storedTenantId) {
      if (brandConfig.tenantId) {
        localStorage.setItem('tenantId', brandConfig.tenantId);
      }
    }
  }, []);

  return (
    <Router>
      <ApolloProvider client={apolloClient}>
        <IpfsGatewayLoader>
          <AuthProvider>
            <ModulePermissionsProvider>
              <ChakraProvider theme={theme}>
                <AppContent />
              </ChakraProvider>
            </ModulePermissionsProvider>
          </AuthProvider>
        </IpfsGatewayLoader>
      </ApolloProvider>
    </Router>
  );
};
